// Central policy for onboarding FLOW action buttons (the menu-btns column).
//
// Single source of truth shared by the owner dashboard (onboarding-dashboard-items)
// and the employee portal (emp-portal-items) so the two views can no longer drift.
// Previously every button's visibility/enablement lived inline as duplicated ng-if
// expressions across both ~490-line templates, and the label/status helpers were
// copy-pasted verbatim into both controllers.
//
// Scope: application-level flow actions (Update, Start Onboarding, Paper/Apex Form,
// Submit, Approve, Terminate, Rehire) plus the shared helpers. Per-form (W4/W9/I9/DD)
// button gating still lives in the templates but now calls the centralized helpers
// here; it can be migrated into POLICIES later using the same pattern.
//
// NOTE: this mirrors the backend OnboardingApplicationStateMachine (CanFire/CurrentStatus).
// A future backend-driven allowed-actions endpoint could replace the predicate bodies
// below without touching the templates or controllers (they only call vm.actions.*).

export type OnboardingView = 'dashboard' | 'empPortal';

export interface OnboardingActionContext {
    view: OnboardingView;
    isOwner: boolean;
    isEmployee: boolean;
    isManager: boolean;
    isHJEE: boolean;
    isSelf?: boolean;
}

export interface OnboardingActionPolicy {
    isVisible: (app: any, ctx: OnboardingActionContext) => boolean;
    isDisabled?: (app: any, ctx: OnboardingActionContext) => boolean;
    label?: string | ((app: any, ctx: OnboardingActionContext) => string);
}

// Forms whose 'Submitted' state re-enables Submit on an already-approved application.
const APPROVED_RESUBMIT_FORMS = ['FederalW4', 'ProvinceW4', 'I9Form', 'W9', 'UserProfile'];

// Module-level so policy predicates can call it without `this` (avoids no-invalid-this in the field initializer).
function isReadyForApproval(app: any): boolean {
    const appStatus = (app.ApplicationStatus === 'Submitted' || app.ApplicationStatus === 'Review');
    const formStatus = ((app.Is1099 === false && app.FederalW4 === 'Approved' && (app.ProvinceW4 === 'Approved' || app.ProvinceW4 === 'NotRequired') && app.I9Form === 'Approved') || (app.Is1099 === true && app.W9 === 'Approved')) && (app.DirectDeposit === 'Approved' || app.DirectDeposit === 'Optional') && app.UserProfile === 'Approved';
    return appStatus && formStatus;
}

// APEX (GvsC/OS merge): owner-only actions (approve / request-review / update / paper toggle).
// The "Self" tab is a pure employee view (is-owner=false), so owner controls never render there;
// an owner approves their OWN profile from the roster tabs (All/Active), where their record shows
// with normal owner controls like any other employee. isSelf is retained on the context for
// backwards compatibility but no longer grants owner powers on its own.
function canOwnerAct(app: any, ctx: OnboardingActionContext): boolean {
    return ctx.isOwner;
}

// APEX-978: a Direct-Deposit-only review (owner pushed back ONLY direct deposit) still lets the owner
// approve the other forms while the employee re-does just their direct deposit. Any other review
// (Employee Details / All Forms) locks per-form approval until the employee resubmits.
export function isDirectDepositOnlyReview(app: any): boolean {
    return app.DirectDeposit === 'ReviewRequired'
        && app.UserProfile !== 'ReviewRequired'
        && app.FederalW4 !== 'ReviewRequired'
        && app.W9 !== 'ReviewRequired'
        && app.ProvinceW4 !== 'ReviewRequired'
        && app.I9Form !== 'ReviewRequired';
}

// APEX-978: whether the owner may approve an individual form now. Mirrors the backend
// ApproveOnboardingForm gate - allowed while Submitted, or (during a Direct-Deposit-only review)
// for any non-DD form. requestTypeId: 5=FederalW4, 2=StateW4, 3=I9, 6=UserProfile, 7=DirectDeposit, -2=W9.
const REQTYPE_TO_FORM_FIELD: { [k: string]: string } = {
    '5': 'FederalW4', '2': 'ProvinceW4', '3': 'I9Form', '6': 'UserProfile', '7': 'DirectDeposit', '-2': 'W9'
};
export function canApproveForm(app: any, requestTypeId: string): boolean {
    const field = REQTYPE_TO_FORM_FIELD[requestTypeId];
    if (field && app[field] === 'Approved') {
        return false;
    }
    if (app.ApplicationStatus === 'Submitted') {
        return true;
    }
    if (app.ApplicationStatus === 'Review') {
        // Direct Deposit can be approved while OTHER forms are under review, as long as DD itself
        // is not the form under review (i.e. it is still Submitted).
        if (requestTypeId === '7') {
            return app.DirectDeposit === 'Submitted';
        }
        // Other forms: only during a Direct-Deposit-only review.
        return isDirectDepositOnlyReview(app);
    }
    // Paper apps: DD may be reviewed/approved by the owner once the employee has submitted it.
    if (app.ApplicationStatus === 'Paper') {
        return requestTypeId === '7' && app.DirectDeposit === 'Submitted';
    }
    return false;
}

export class OnboardingActionPolicyService {

    pendingText = 'Pending Changes';
    resignText = 'Resign Required';
    submitText = 'Changes Submitted';

    // One entry per flow action. Predicates are translated 1:1 from the original
    // template ng-if / ng-disabled expressions; do not "clean up" the logic here —
    // this is a behavior-preserving refactor.
    private policies: { [action: string]: OnboardingActionPolicy } = {

        // Owner: edit/update employee (full onboarding modal). Hidden at Paper -- Paper apps use
        // updateLegacyProfile (lightweight roster edit) instead. Non-legacy: hidden when missing info + active (Start Onboarding shows). Legacy: hidden when missing info + active; terminated legacy always shown.
        // does backnote: Allowing Update from review status
        update: {
            isVisible: (app, ctx) => canOwnerAct(app, ctx)
                && ['Approved', 'Submitted', 'Created', 'Pending', 'Started', 'Review'].includes(app.ApplicationStatus)
                && (!app.IsLegacy
                    // non-legacy + missing info + not terminated: hide Update so only Start Onboarding shows
                    ? !(app.MissingOnboardingInfo && app.EmployeeStatus !== 'Terminated')   
                    : (!app.MissingOnboardingInfo || app.EmployeeStatus === 'Terminated')),
            label: 'Update'
        },

        // APEX-977 - Employee: profile-level Update. Opens the onboarding-update-modal to re-open a
        // chosen group of already-submitted/approved forms. Employee-gated (the owner uses `update` above).
        // Shown once there is something to re-open; hidden for Paper apps.
        employeeUpdate: {
            isVisible: (app, ctx) => ctx.isEmployee && !canOwnerAct(app, ctx)
                && (
                    (['Started', 'Pending', 'Review', 'Approved'].includes(app.ApplicationStatus)
                        && app.ApplicationStatus !== 'Paper')
                    // Paper apps: employee may re-open Direct Deposit only (once there is a submitted/approved DD to update).
                    || (app.ApplicationStatus === 'Paper'
                        && ['Submitted', 'Approved'].includes(app.DirectDeposit))
                ),
            label: 'Update'
        },

        // Owner: lightweight roster profile edit (navigation only) for ANY Paper application, legacy or non-legacy. Replaces the full Update modal at Paper.
        updateLegacyProfile: {
            isVisible: (app, ctx) => ctx.isOwner && app.ApplicationStatus === 'Paper',
            label: 'Update'
        },

        // Owner: kick off onboarding for a telemapper-linked legacy employee missing info.
        // Hidden for ALL terminated employees (they get Update / Switch to Paper instead).
        startOnboarding: {
            isVisible: (app, ctx) => ctx.isOwner
                && app.MissingOnboardingInfo && app.ApplicationStatus !== 'Paper'
                && app.EmployeeStatus !== 'Terminated',
            label: 'Start Onboarding'
        },

        // Owner: switch application to paper.
        // KNOWN DIVERGENCE (preserved, flagged for product review): the employee portal
        // additionally excludes 'Pending' from the eligible statuses; the dashboard does not.
        changeToPaper: {
            isVisible: (app, ctx) => {
                // Terminated (legacy or non-legacy): offer Switch to Paper (in place of Start Onboarding),
                // ignoring missing-info and including Approved. Backed by a matching
                // SwitchToPaper transition from Approved in the backend state machine.
                if (ctx.isOwner && app.EmployeeStatus === 'Terminated') {
                    return ['Created', 'Started', 'Pending', 'Approved', 'Submitted'].includes(app.ApplicationStatus);
                }
                const excluded = ['Approved', 'Review', 'Submitted', 'Paper', 'Deleted'];
                return canOwnerAct(app, ctx) && !excluded.includes(app.ApplicationStatus) && (!app.MissingOnboardingInfo);
            },
            label: 'Paper Form'
        },

        // Owner: switch a paper application back to online.
        changeToOnline: {
            isVisible: (app, ctx) => canOwnerAct(app, ctx) && app.ApplicationStatus === 'Paper'
                && app.EmployeeStatus !== 'Terminated',
            label: 'Digital Form'
        },

        // Employee: submit the application for approval.
        submit: {
            isVisible: (app, ctx) => ctx.isEmployee
                && (app.ApplicationStatus !== 'Approved'
                    || (app.ApplicationStatus === 'Approved' && app.Complete
                        && APPROVED_RESUBMIT_FORMS.some(f => app[f] === 'Submitted')))
                && app.ApplicationStatus !== 'Submitted'
                && app.ApplicationStatus !== 'Paper',
            isDisabled: (app) => !app.Complete,
            label: 'Submit'
        },

        // Employee: read-only "Submitted" indicator.
        submitted: {    
            isVisible: (app, ctx) => ctx.isEmployee && !canOwnerAct(app, ctx) && app.ApplicationStatus === 'Submitted',
            isDisabled: () => true,
            label: 'Submitted'
        },

        // Owner: approve the application (disabled until all required forms are approved).
        approve: {
            isVisible: (app, ctx) => canOwnerAct(app, ctx) && app.ApplicationStatus !== 'Approved'
                && (app.ApplicationStatus === 'Submitted' || app.ApplicationStatus === 'Review')
                && app.ApplicationStatus !== 'Paper',
            isDisabled: (app) => !isReadyForApproval(app) || app.ApplicationStatus === 'Review',
            label: 'Approve'
        },

        // APEX-978 - Owner: push selected forms back to the employee for changes.
        // Opens the request-review modal (grouped forms + comment). Only offered on a
        // submitted application awaiting approval; once pushed back the app is in Review.
        requestReview: {
            isVisible: (app, ctx) => canOwnerAct(app, ctx)
                && (app.ApplicationStatus === 'Submitted'
                    || (app.ApplicationStatus === 'Paper' && app.DirectDeposit === 'Submitted')
                    || (app.ApplicationStatus === 'Approved' && app.EmployeeStatus !== 'Terminated')), // Paper: DD-only; Approved: ER re-opens for changes
            label: 'Request Review'
        },

        // Read-only "Approved" indicator (any role).
        approved: {
            isVisible: (app) => app.ApplicationStatus === 'Approved' && app.EmployeeStatus !== 'Terminated',
            isDisabled: () => true,
            label: 'Approved'
        },

        // Owner: terminate the employee.
        terminate: {
            isVisible: (app, ctx) => ctx.isOwner && app.ApplicationStatus !== 'Deleted'
                && app.EmployeeStatus !== 'Terminated',
            label: 'Terminate'
        },

        // Owner: rehire a terminated employee.
        rehire: {
            isVisible: (app, ctx) => ctx.isOwner && app.EmployeeStatus === 'Terminated',
            label: 'Rehire'
        }
    };

    isVisible(action: string, app: any, ctx: OnboardingActionContext): boolean {
        const p = this.policies[action];
        return !!p && p.isVisible(app, ctx);
    }

    isDisabled(action: string, app: any, ctx: OnboardingActionContext): boolean {
        const p = this.policies[action];
        return !!p && !!p.isDisabled && p.isDisabled(app, ctx);
    }

    label(action: string, app: any, ctx: OnboardingActionContext): string {
        const p = this.policies[action];
        if (!p || p.label == null) {
            return '';
        }
        return typeof p.label === 'function' ? p.label(app, ctx) : p.label;
    }

    // ----- Shared helpers (previously duplicated verbatim in both controllers) -----

    getButtonTitle(requestTypeId, app) {
        switch (requestTypeId) {
            case "6":
                return app.ApplicationStatus === 'Submitted' ? "View" : (app.UserProfile === 'Review' || app.UserProfile === 'Submitted' || app.UserProfile === 'Approved' || (app.ApplicationStatus === 'Approved' && app.EmployeeStatus === 'Active')) ? "View" : "Fill";
            case "5":
                return app.ApplicationStatus === 'Submitted' ? "View" : app.FederalW4 === 'Review' || app.FederalW4 === 'Submitted' || app.FederalW4 === 'Approved' || (app.ApplicationStatus === 'Approved' && app.EmployeeStatus === 'Active') ? "View" : "Fill";
            case "-2":
                return app.ApplicationStatus === 'Submitted' ? "View" : app.W9 === 'Review' || app.W9 === 'Submitted' || app.W9 === 'Approved' || (app.ApplicationStatus === 'Approved' && app.EmployeeStatus === 'Active') ? "View" : "Fill";
            case "2": // ProvinceW4
                return app.ApplicationStatus === 'Submitted' ? "View" : app.ProvinceW4 === 'Review' || app.ProvinceW4 === 'Submitted' || app.ProvinceW4 === 'Approved' || (app.ApplicationStatus === 'Approved' && app.EmployeeStatus === 'Active') ? "View" : "Fill";
            case "3": // I9
                return app.ApplicationStatus === 'Submitted' ? "View" : app.I9Form === 'Review' || app.I9Form === 'Submitted' || app.I9Form === 'Approved' || (app.ApplicationStatus === 'Approved' && app.EmployeeStatus === 'Active') ? "View" : "Fill";
            case "7": // DirectDeposit
                return app.ApplicationStatus === 'Submitted' ? "View" : app.DirectDeposit === 'Review' || app.DirectDeposit === 'Submitted' || app.DirectDeposit === 'Approved' || (app.ApplicationStatus === 'Approved' && app.EmployeeStatus === 'Active') ? "View" : "Fill";
            case "confirmation":
                return "";
        }
    }

    updateFormStatus(app, type) {
        var title = ''
        switch (type) {
            case "5":
                if (app.EmployeeStatus === 'Active' && app.FederalW4 === "Pending" && app.IsApprovedByOwner === true && app.HasSavedInfo != null && app.HasSavedInfo.includes(-1)) {
                    title = this.pendingText;
                } else if (app.EmployeeStatus === 'Active' && app.FederalW4 === "Submitted" && app.IsApprovedByOwner === true) {
                    title = this.submitText;
                } else if (app.EmployeeStatus === 'Active' && app.FederalW4 === "Pending" && app.IsApprovedByOwner === true) {
                    title = this.resignText;
                } else {
                    title = app.FederalW4;
                }
                break;
            case "-2":
                if (app.EmployeeStatus === 'Active' && app.W9 === "Pending" && app.IsApprovedByOwner === true && app.HasSavedInfo != null && app.HasSavedInfo.includes(-2)) {
                    title = this.pendingText;
                } else if (app.EmployeeStatus === 'Active' && app.W9 === "Submitted" && app.IsApprovedByOwner === true) {
                    title = this.submitText;
                } else if (app.EmployeeStatus === 'Active' && app.W9 === "Pending" && app.IsApprovedByOwner === true) {
                    title = this.resignText;
                } else {
                    title = app.W9;
                }
                break;
            case "3":
                if (app.EmployeeStatus === 'Active' && app.I9Form === "Pending" && app.IsApprovedByOwner === true && app.HasSavedInfo != null && app.HasSavedInfo.includes(0)) {
                    title = this.pendingText;
                } else if (app.EmployeeStatus === 'Active' && app.I9Form === "Submitted" && app.IsApprovedByOwner === true) {
                    title = this.submitText;
                } else if (app.EmployeeStatus === 'Active' && app.I9Form === "Pending" && app.IsApprovedByOwner === true) {
                    title = this.resignText;
                } else {
                    title = app.I9Form;
                }
                break;
            case "2":
                if (app.EmployeeStatus === 'Active' && app.ProvinceW4 === "Pending" && app.IsApprovedByOwner === true && app.HasSavedInfo != null && app.HasSavedInfo.some(function (x) {
                    return x !== -2 && x !== -1 && x !== 0;
                })) {
                    title = this.pendingText;
                } else if (app.EmployeeStatus === 'Active' && app.ProvinceW4 === "Submitted" && app.IsApprovedByOwner === true) {
                    title = this.submitText;
                } else if (app.EmployeeStatus === 'Active' && app.ProvinceW4 === "Pending" && app.IsApprovedByOwner === true) {
                    title = this.resignText;
                } else {
                    title = app.ProvinceW4;
                }
                // State-W4 exempt/withhold choice: append to the existing Submitted/Approved status.
                if (app.ProvinceW4ExemptChoice === 'true') {
                    title = title + ' (Exempt)';
                } else if (app.ProvinceW4ExemptChoice === 'false') {
                    title = title + ' (Withhold)';
                }
                break;
        }
        return title;
    }

    readyForApproval(app) {
        return isReadyForApproval(app);
    }

    getDeleteLabel(app) {
        return (app.IsTelemapperLinked ? "Terminate" : ((app.ApplicationStatus !== 'Created') ? "Terminate" : "Terminate"));
    }
}
